import { useMemo, type VoidFunctionComponent } from 'react';
import type { InputActionMeta } from 'react-select';

import { getTypeaheadQuery } from '../../../../shared/registrySearch/capabilities';
import { useSearchPackages } from '../../../hooks/api/registrySearch/useSearchPackages';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import FormLabel from '../../generic/Forms/FormLabel';
import Select from '../../generic/Select/Select';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';
import type { PackageHit } from '../../../../shared/registrySearch';

import { NO_DROPDOWN_COMPONENTS } from './selectIndicators';

import styles from './PackageAutocomplete.module.less';

const MIN_SEARCH_CHARS = 3;

type PackageOption = {
  value: string;
  label: string;
  description?: string;
  fromTypeahead: boolean;
};

// Extracted to module scope (instead of inlining in JSX) to avoid `react/no-unstable-nested-components`.
const formatOptionLabel = (opt: PackageOption) => (
  <div className={styles.optionRow}>
    <div className={styles.optionLabel}>{opt.label}</div>
    {opt.description && <div className={styles.optionSecondary}>{opt.description}</div>}
  </div>
);

type Props = {
  id: string;
  fetcher: ThirdPartyFetcher;
  value: string;
  /**
   * Emitted with the selected name and a `fromTypeahead` flag indicating whether the value came
   * from a search-result pick (true) or a user-typed entry (false). The parent uses the flag to
   * decide whether to skip the Test step (typeahead picks are validated by construction).
   */
  onChange: (name: string, fromTypeahead: boolean) => void;
  /**
   * Fired when the field loses focus. The parent uses this to "commit" the current input as the
   * package whose versions to fetch, so a version lookup doesn't fire for every partial keystroke.
   */
  onBlur?: () => void;
  /**
   * Whether the typeahead dropdown should engage for the current input value. When false, the
   * field still accepts typed input (manual entry) but skips the fetch and keeps the dropdown
   * closed — used when the input doesn't match a host shape our backend can autocomplete
   * (e.g. a `gitlab.com/...` git locator while only GitHub is supported).
   */
  isMenuEnabled?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
};

/**
 * Text-input-with-autocomplete UX. Uses sync `Select` with externally-managed options (rather
 * than `AsyncSelect`'s `loadOptions`) so we control the fetch / debounce / cache ourselves and
 * sidestep react-select's async loading model — which only fetches on user keystrokes, leaving
 * stale or empty menus when an externally-controlled `inputValue` changes (e.g. on focus after
 * a previous pick).
 */
export const PackageNameAutocomplete: VoidFunctionComponent<Props> = ({
  id, fetcher, value, onChange, onBlur, isMenuEnabled = true, isDisabled, isRequired,
}) => {
  // Debounce the typed value so per-keystroke typing doesn't spam the registry-search backend;
  // the debounced value drives the package-list fetch via useSearchPackages.
  const { debouncedValue: debouncedQuery } = useDebouncedValue(value);

  // The post-prefix typeahead query — for host-prefixed fetchers (e.g. `github.com/`), the
  // prefix doesn't count toward the threshold; we wait for >=MIN_SEARCH_CHARS user-entered
  // characters of the actual query tail. The full `debouncedQuery` (with prefix) is what's
  // sent on the wire — the backend's per-impl `supports?` routing needs the host context to
  // pick the right RegistryServer, then strips its prefix internally before forwarding to
  // the upstream registry's search endpoint.
  const typeaheadQuery = getTypeaheadQuery(fetcher, debouncedQuery);
  const isQueryReady = isMenuEnabled && typeaheadQuery !== null && typeaheadQuery.trim().length >= MIN_SEARCH_CHARS;

  const { data, isLoading } = useSearchPackages(fetcher, debouncedQuery, isQueryReady);

  const options = useMemo<PackageOption[]>(() => {
    const hits: PackageHit[] = data?.results ?? [];
    return hits.map((hit) => ({
      value: hit.name,
      label: hit.name,
      description: hit.description,
      fromTypeahead: true,
    }));
  }, [data]);

  return (
    <FormLabel label="Name" htmlFor={`${id}-input`} requiredText={isRequired ? '(Required)' : undefined}>
      <Select<PackageOption>
        id={id}
        inputId={`${id}-input`}
        className={styles.alwaysVisibleInput}
        // Keep react-select's internal selected-option state always null. We use this as a
        // controlled-input typeahead (we own `inputValue` and bubble picks via `onChange`); we
        // never read react-select's `value` ourselves, so letting it accumulate a stale
        // selection would only be a source of subtle bugs (e.g. previously-picked option
        // re-highlighting on menu reopen, or keyboard nav landing on a stale row).
        value={null}
        // Controlled-input mode: the input renders the typed/picked text directly (not a
        // non-editable chip), so users can re-focus and continue editing. Without this,
        // re-focusing after a pick would put the cursor at position 0 and edits would feel
        // broken — particularly when editing a picked value such that it no longer matches a
        // host prefix.
        inputValue={value}
        controlShouldRenderValue={false}
        // When the input shape isn't autocompletable, force the menu closed (manual-entry mode)
        // and strip the dropdown indicators so the field reads as a plain input.
        menuIsOpen={isMenuEnabled ? undefined : false}
        components={isMenuEnabled ? undefined : NO_DROPDOWN_COMPONENTS}
        options={options}
        // The server already searched against the upstream registry — registries match against
        // fields the option label doesn't carry (description, README, owner login). Passing
        // `null` disables react-select's default substring filter on the label so the dropdown
        // shows all server-returned hits in the order the registry chose.
        filterOption={null}
        // Gate the spinner on `isQueryReady` — react-query's `isLoading` can stay truthy
        // briefly during queryKey transitions even when `enabled` flips false, which would
        // show a misleading throbber while typing a value our backend won't search.
        isLoading={isQueryReady && isLoading}
        onBlur={onBlur}
        onInputChange={(input: string, meta: InputActionMeta) => {
          // Only react to user typing; ignore react-select's internal 'set-value' /
          // 'menu-close' / 'input-blur' actions so the displayed text isn't wiped after a
          // pick or blur.
          if (meta.action === 'input-change') {
            onChange(input, false);
          }
        }}
        onChange={(opt) => {
          if (opt) {
            onChange(opt.value, true);
          }
        }}
        formatOptionLabel={formatOptionLabel}
        placeholder="Enter a package name"
        isDisabled={isDisabled}
        noOptionsMessage={({ inputValue }) => {
          // Show the post-prefix query in the message so feedback matches what we actually
          // searched for ("No matches for 'lodash'", not "...for 'github.com/lodash'").
          const q = getTypeaheadQuery(fetcher, inputValue)?.trim() ?? '';
          if (q.length < MIN_SEARCH_CHARS) {
            return `Type at least ${MIN_SEARCH_CHARS} characters to search`;
          }
          return `No matches for "${q}"`;
        }}
      />
    </FormLabel>
  );
};
